import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Box, Chip, Paper, Typography, IconButton, useTheme, keyframes } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import { SeverityChip } from 'components/shared/SeverityChip';
import { getSeverityColorFromTheme } from 'components/shared/utils/colorUtils';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import type { AttentionCamera } from '../../types/canvision.types';
import type { Theme } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Pulsing border animation for highlighted tiles
// ---------------------------------------------------------------------------

const pulseGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.6); }
  50%  { box-shadow: 0 0 12px 4px rgba(33, 150, 243, 0.35); }
  100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.6); }
`;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AttentionFeedTileProps {
  /** Camera data to display */
  camera: AttentionCamera;
  /** Called when the dismiss (X) button is clicked */
  onDismiss?: (cameraId: number) => void;
  /** Called when the tile body is clicked (opens CameraDetail modal) */
  onClick?: (camera: AttentionCamera) => void;
  /** Whether this tile should show the highlighted pulsing border */
  isHighlighted?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extended severity-to-color mapper that handles 'high' severity
 * (which the base colorUtils maps to grey by default).
 * - critical -> error.main (red)
 * - high     -> warning.main (orange)
 */
function getAttentionSeverityColor(severity: string, theme: Theme): string {
  if (severity.toLowerCase() === 'high') {
    return theme.palette.warning.main;
  }
  return getSeverityColorFromTheme(severity, theme);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Individual camera tile for the Attention Feed grid.
 *
 * Renders a 16:9 video player with overlay information:
 *  - Top-left:     Dismiss (X) IconButton — visible on hover
 *  - Top-right:    SeverityChip (uses camera.highestSeverity)
 *  - Bottom-left:  Camera name + site name
 *  - Bottom-right: Event count badge (when > 1) + latest event type
 *
 * The left border is colored by the highest severity across all active events.
 */
export function AttentionFeedTile({ camera, onDismiss, onClick, isHighlighted = false }: AttentionFeedTileProps) {
  const theme = useTheme();
  const severityColor = getAttentionSeverityColor(camera.highestSeverity, theme);
  const eventCount = camera.events?.length ?? 0;

  // -- Viewport intersection detection ----------------------------------------
  // Only render the VideoPlayer when the tile is visible in the viewport.
  // When scrolled out of view, the HLS instance is unmounted and cleaned up.
  const tileRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = tileRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { rootMargin: '100px', threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // -- Handlers ---------------------------------------------------------------

  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // prevent onClick on tile
      onDismiss?.(camera.cameraId);
    },
    [onDismiss, camera.cameraId],
  );

  const handleClick = useCallback(() => {
    onClick?.(camera);
  }, [onClick, camera]);

  // -- Render -----------------------------------------------------------------

  return (
    <Paper
      ref={tileRef}
      elevation={1}
      onClick={handleClick}
      sx={{
        borderLeft: `4px solid ${severityColor}`,
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 1,
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        // Pulsing glow when highlighted via event/incident row hover
        ...(isHighlighted && {
          border: `2px solid ${theme.palette.info.main}`,
          borderLeft: `4px solid ${theme.palette.info.main}`,
          animation: `${pulseGlow} 1.5s ease-in-out infinite`,
        }),
        '&:hover': {
          boxShadow: theme.shadows[4],
          '& .attention-tile-dismiss': {
            opacity: 1,
          },
        },
      }}
    >
      {/* 16:9 aspect ratio container */}
      <Box sx={{ position: 'relative', paddingTop: '56.25%', backgroundColor: 'grey.900' }}>
        {/* Video player — positioned absolute to fill the container */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          {camera.streamUrl && isVisible ? (
            <VideoPlayer
              streamUrl={camera.streamUrl}
              streamType={camera.streamType || 'hls'}
              autoPlay
              muted
            />
          ) : (
            /* Placeholder when not visible or no stream URL */
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'grey.500',
              }}
            >
              {camera.streamUrl ? (
                <VideocamIcon sx={{ fontSize: 32, opacity: 0.4 }} />
              ) : (
                <Typography variant="caption">No stream</Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Gradient overlays for text readability */}
        {/* Top gradient */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '40%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* Bottom gradient */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />

        {/* Dismiss button — top-left, visible on hover */}
        <IconButton
          className="attention-tile-dismiss"
          size="small"
          onClick={handleDismiss}
          aria-label={`Dismiss ${camera.cameraName}`}
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            opacity: 0,
            transition: 'opacity 0.2s ease',
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.4)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.7)',
            },
            zIndex: 1,
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {/* Severity chip — top-right */}
        <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 1 }}>
          <SeverityChip severity={camera.highestSeverity} size="small" />
        </Box>

        {/* Camera name + site name — bottom-left */}
        <Box sx={{ position: 'absolute', bottom: 6, left: 8, zIndex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'white',
              fontWeight: 600,
              display: 'block',
              lineHeight: 1.3,
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
            }}
          >
            {camera.cameraName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.65rem',
              lineHeight: 1.2,
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
            }}
          >
            {camera.siteName}
          </Typography>
        </Box>

        {/* Event count badge + event type — bottom-right */}
        <Box sx={{ position: 'absolute', bottom: 6, right: 8, zIndex: 1, textAlign: 'right' }}>
          {eventCount > 1 && (
            <Chip
              label={`${eventCount} events`}
              size="small"
              sx={{
                height: 18,
                mb: 0.25,
                fontSize: '0.6rem',
                fontWeight: 600,
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'common.white',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: '0.65rem',
              fontWeight: 500,
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
              textTransform: 'capitalize',
              display: 'block',
            }}
          >
            {camera.latestEventType}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default AttentionFeedTile;
